import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SeverityDot } from "@/components/ui/severity-dot";
import { FindingPolarityMark } from "@/components/experience/finding-polarity-mark";
import type { Severity } from "./types";

interface LiveWalkthroughFooterProps {
  totalFindings: number;
  severityCounts: Record<Severity, number>;
  helpsCount: number;
  onSelectFindingFilter: (filter: Severity | "all" | "helps") => void;
  hasHtmlReport: boolean;
  onExportReport: () => void;
  isComplete: boolean;
  projectId: string;
  walkthroughId: string;
}

export function LiveWalkthroughFooter({
  totalFindings,
  severityCounts,
  helpsCount,
  onSelectFindingFilter,
  hasHtmlReport,
  onExportReport,
  isComplete,
  projectId,
  walkthroughId,
}: LiveWalkthroughFooterProps) {
  return (
    <footer className="flex flex-col sm:flex-row shrink-0 items-start sm:items-center justify-between gap-3 border-t border-border/60 bg-background px-4 sm:px-6 py-3">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs sm:text-sm text-muted-foreground">
        <button
          type="button"
          onClick={() => onSelectFindingFilter("all")}
          className="hover:text-foreground transition-colors inline-flex items-center gap-1 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring rounded"
          title="View all findings"
        >
          <output className="font-medium tabular-nums text-foreground">
            {totalFindings}
          </output>{" "}
          finding{totalFindings !== 1 ? "s" : ""}
        </button>

        {severityCounts.critical > 0 && (
          <button
            type="button"
            onClick={() => onSelectFindingFilter("critical")}
            className="inline-flex items-center gap-1.5 hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring rounded"
            title="Filter to critical findings"
          >
            <SeverityDot tone="critical" />
            <span className="tabular-nums font-medium text-foreground">
              {severityCounts.critical}
            </span>{" "}
            critical
          </button>
        )}

        {severityCounts.major > 0 && (
          <button
            type="button"
            onClick={() => onSelectFindingFilter("major")}
            className="inline-flex items-center gap-1.5 hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring rounded"
            title="Filter to major findings"
          >
            <SeverityDot tone="important" />
            <span className="tabular-nums font-medium text-foreground">
              {severityCounts.major}
            </span>{" "}
            major
          </button>
        )}

        {severityCounts.minor > 0 && (
          <button
            type="button"
            onClick={() => onSelectFindingFilter("minor")}
            className="inline-flex items-center gap-1.5 hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring rounded"
            title="Filter to minor findings"
          >
            <SeverityDot tone="minor" />
            <span className="tabular-nums font-medium text-foreground">
              {severityCounts.minor}
            </span>{" "}
            minor
          </button>
        )}

        {helpsCount > 0 && (
          <button
            type="button"
            onClick={() => onSelectFindingFilter("helps")}
            className="inline-flex items-center gap-1.5 hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring rounded"
            title="Filter to strengths"
          >
            <FindingPolarityMark type="helps" />
            <span className="tabular-nums font-medium text-foreground">
              {helpsCount}
            </span>{" "}
            strengths
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {hasHtmlReport && (
          <Button variant="outline" size="sm" onClick={onExportReport}>
            Export report
          </Button>
        )}
        {isComplete && (
          <Button asChild size="sm">
            <Link to={`/projects/${projectId}/walkthroughs/${walkthroughId}`}>
              View full report
              <ArrowRight className="ml-1.5 h-3.5 w-3.5" aria-hidden="true" />
            </Link>
          </Button>
        )}
      </div>
    </footer>
  );
}
